import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthDAO from '../storage/AuthDAO';
import { AuthController } from './AuthController';
import { getCachedDeviceId } from '../utils/CachedDeviceInfo';

class HttpService {

    private axiosInstance: AxiosInstance;
    constructor(baseURL: string | undefined, applicationId: string | undefined) {
        this.axiosInstance = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
                'X-Pto-Bc': applicationId,
            },
        });

        this.axiosInstance.interceptors.request.use(async (config) => {

            const deviceId = await getCachedDeviceId();
            const auth = AuthDAO.getAuth();
            
            if (deviceId) {
                config.headers['X-Device-Id'] = deviceId;
            }

            if (auth && auth?.accessToken) {
                 config.headers['Authorization'] = `Bearer ${auth.accessToken}`;
            } else {
                 delete config.headers['Authorization'];
            }
            
            return config;
        }, function (error) {
            // Do something with request error
            return Promise.reject(error);
        });

        this.axiosInstance.interceptors.response.use(function (response) {
            // Any status code that lie within the range of 2xx cause this function to trigger
            // Do something with response data
            return response;
        }, async (error) => {
            if (error.response) {
                //Handle license validation errors
                const { headers, status, config } = error.response;
                if (status === 401 || status === 403) {
                    const errorCode = headers['error-code'];

                    if (status === 403 && !errorCode) {
                        let authData = AuthDAO.getAuth();
                        if (!authData) {
                            await callSignOut('session_expired');
                            return Promise.reject(error);
                        }
                    }

                    switch (errorCode) {
                        case '00001':
                        case '00002':
                            await callSignOut('license_expired');
                            return Promise.reject(error);
                        case '00003':
                            await callSignOut('max_users_reached');
                            return Promise.reject(error);
                        case '00004':
                            let success = await callRefreshToken(config); // Assuming you have a function to refresh the token
                            if (success) {
                                return this.axiosInstance(config); // Retry the request with the new token
                            }
                            return Promise.reject(error);
                        case '00005':
                            return Promise.reject(error);
                        case '00006':
                            await callSignOut('invalid_credentials');
                            return Promise.reject(error);
                        default:
                            alert('Internal server error! If the problem persists, contact support.');
                            break;
                    }
                }
            }

            return Promise.reject(error);
        });
    }

    public get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.get<T>(url, config);
    }

    public post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.post<T>(url, data, config);
    }

    public put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.put<T>(url, data, config);
    }

    public delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.delete<T>(url, config);
    }
}

const BASE_URL = process.env.REACT_APP_API_URL;
const APP_ID = process.env.REACT_APP_APPLICATION_ID_WEB;

const httpService = new HttpService(BASE_URL, APP_ID);

export { httpService };

async function callRefreshToken(config: AxiosRequestConfig): Promise<boolean> {
    try {

        const authData = AuthDAO.getAuth();
        if (!authData || !authData.refreshToken) {
            console.error('No auth data or refresh token available');
            return Promise.resolve(false);
        }

        let refreshToken = authData.refreshToken;
        AuthDAO.clear(); // prevent default authorization header

        const refreshResponse = await axios.post(`${BASE_URL}/auth/refresh-token`, {
            refreshToken: refreshToken,
        });

        if (refreshResponse.status === 200) {
            const newAuth = refreshResponse.data;

            // Update the context with the new token
            AuthDAO.setAuth({ ...newAuth });

            return Promise.resolve(true);
        }

    } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        await callSignOut('session_expired');
    }
    return Promise.resolve(false);
}

async function callSignOut(reason: string) {
    try {
        await AuthController.signOut(reason);
    } catch (error) {
        console.error('Sign out failed:', error);
    }
}   
